import { useState, useMemo, useEffect } from "react";
import {
  AccessibilityMode,
  OverlayType,
  HubTab,
  HelpTab,
  UserExposureLevel,
  ActiveMission,
} from "../../types";

const STORAGE_EXPOSURE_KEY = "gsc_user_exposure";
const STORAGE_ANALYSIS_COUNT_KEY = "gsc_analysis_count";

function safeGetLocalStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetLocalStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore (private mode / blocked storage)
  }
}

function isValidExposureLevel(v: any): v is UserExposureLevel {
  return v === "FREE" || v === "TRIAL" || v === "PREMIUM";
}

export function useUIState() {
  const [errorMessage, setErrorMessage] = useState("");
  const [showSubscriptionOffer, setShowSubscriptionOffer] = useState(false);
  const [showSupportHub, setShowSupportHub] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  const [activeOverlay, setActiveOverlay] = useState<OverlayType>("NONE");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hubTab, setHubTab] = useState<HubTab>("SONGBOOK");
  const [helpTab, setHelpTab] = useState<HelpTab>("MANUAL");

  const [activeMission, setActiveMission] = useState<ActiveMission | null>(null);

  const [accessibilityMode, setAccessibilityMode] =
    useState<AccessibilityMode>("STANDARD");

  // Exposure level: "FREE" | "TRIAL" | "PREMIUM"
  const [exposureLevel, setExposureLevel] = useState<UserExposureLevel>(() => {
    const saved = safeGetLocalStorage(STORAGE_EXPOSURE_KEY);
    if (isValidExposureLevel(saved)) return saved;
    return "FREE";
  });

  useEffect(() => {
    const raw = safeGetLocalStorage(STORAGE_ANALYSIS_COUNT_KEY) || "0";
    const count = Number.parseInt(raw, 10) || 0;

    // Simple auto-progression based on analysis count
    // FREE -> TRIAL at 3 analyses
    // TRIAL -> PREMIUM at 10 analyses
    if (exposureLevel === "FREE" && count >= 3) {
      setExposureLevel("TRIAL");
      safeSetLocalStorage(STORAGE_EXPOSURE_KEY, "TRIAL");
    } else if (exposureLevel === "TRIAL" && count >= 10) {
      setExposureLevel("PREMIUM");
      safeSetLocalStorage(STORAGE_EXPOSURE_KEY, "PREMIUM");
    }
  }, [exposureLevel]);

  const setExposureLevelManually = (level: UserExposureLevel) => {
    setExposureLevel(level);
    safeSetLocalStorage(STORAGE_EXPOSURE_KEY, level);
  };

  const [isCoachTransition, setIsCoachTransition] = useState(false);
  const [coachTransitionLabel, setCoachTransitionLabel] = useState<string | null>(
    null
  );

  return useMemo(
    () => ({
      errorMessage,
      setErrorMessage,
      showSubscriptionOffer,
      setShowSubscriptionOffer,
      showSupportHub,
      setShowSupportHub,
      showTutorial,
      setShowTutorial,
      activeOverlay,
      setActiveOverlay,
      isMenuOpen,
      setIsMenuOpen,
      hubTab,
      setHubTab,
      helpTab,
      setHelpTab,
      activeMission,
      setActiveMission,
      isCoachTransition,
      setIsCoachTransition,
      coachTransitionLabel,
      setCoachTransitionLabel,
      accessibilityMode,
      setAccessibilityMode,
      exposureLevel,
      setExposureLevel: setExposureLevelManually,
    }),
    [
      errorMessage,
      showSubscriptionOffer,
      showSupportHub,
      showTutorial,
      activeOverlay,
      isMenuOpen,
      hubTab,
      helpTab,
      activeMission,
      isCoachTransition,
      coachTransitionLabel,
      accessibilityMode,
      exposureLevel,
    ]
  );
}
